import { FC, useMemo } from 'react';

import CustomMultiSelect from '@/components/Filtering/CustomMultiSelect.tsx';

import useConfig from '@/hooks/useConfig.ts';
import useFilters from '@/hooks/useFilters.ts';

import { useFilterStore } from '@/stores/filterStore.ts';

const RoomSelect: FC = () => {
    const { location_key } = useConfig();
    const { room_options } = useFilters();
    const selected_rooms = useFilterStore((state) => state.selected_rooms);
    const current_selected_rooms = selected_rooms[location_key] ?? [];

    // Memoized display text for the input should come from translations
    const display_text = useMemo(() => {
        if (current_selected_rooms.length === 0) return 'Keine Räume';
        if (current_selected_rooms.length === room_options.length) return `Alle Räume (${room_options.length})`;
        return `Ausgewählte Räume (${current_selected_rooms.length})`;
    }, [current_selected_rooms.length, room_options.length]);

    // Handler for selecting/deselecting all rooms
    const handleSelectAll = (): void => {
        if (current_selected_rooms.length === room_options.length) {
            useFilterStore.setState((state) => ({
                selected_rooms: {
                    ...state.selected_rooms,
                    [location_key]: [],
                },
            }));
        } else {
            useFilterStore.setState((state) => ({
                selected_rooms: {
                    ...state.selected_rooms,
                    [location_key]: room_options.map((option) => option.value),
                },
            }));
        }
    };

    // Handler for toggling individual room selection
    const handleToggleRoom = (room_value: string): void => {
        const new_selected = current_selected_rooms.includes(room_value)
            ? current_selected_rooms.filter((value) => value !== room_value)
            : [...current_selected_rooms, room_value];

        useFilterStore.setState((state) => ({
            selected_rooms: {
                ...state.selected_rooms,
                [location_key]: new_selected,
            },
        }));
    };

    return (
        <CustomMultiSelect
            label="Alle Räume"
            displayText={display_text}
            selected={current_selected_rooms}
            options={room_options}
            handleToggle={handleToggleRoom}
            handleSelectAll={handleSelectAll}
        />
    );
};

export default RoomSelect;
